<template>
  <blog-post-v2 :post="post as BlogPost" :related-posts="relatedPosts" />
</template>

<script setup lang="ts">
import { onBeforeMount, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { getTitle } from "@/utils/title";
import { amplitudePageViewTrack } from "@/utils/amplitude";
import { getDate } from "@/utils/datetimeHelper";
import { insertScript } from "@/utils/domHelper";
import { changeLanguage } from "@/i18n.js";
import BlogPostV2 from "@/components/v2/blog-post/index.vue";
import { BlogPost } from "@/interfaces/Blog/BlogPost.ts";
import { deleteCookie, setCookie } from "@/utils/cookies.ts";
import { useBlogPostStore } from "@/store/blogPost.ts";

defineOptions({
  name: "blog-post-page",
});

const props = defineProps({
  alias: {
    type: String,
    required: true,
  },
});

const post = ref<BlogPost | null>(null);
const relatedPosts = ref<BlogPost[]>([]);
const route = useRoute();
const router = useRouter();
const { locale } = useI18n();
const blogPostStore = useBlogPostStore();

watch(locale, (val) => {
  let path;

  if (val === "de") {
    path = route.path.replace("/blog", "/lecker_blog");
  } else {
    path = route.path.replace("/lecker_blog", "/blog");
  }

  router.replace({
    ...route,
    ...{ path: path },
  });
});

watch(
  route,
  (value, oldValue) => {
    if (value.path === oldValue.path) {
      return;
    }

    load();
  },
  { deep: true },
);

async function load() {
  if (route.query.sfw) {
    if (route.query.sfw === "1") {
      setCookie("av_enabled", "1");
    } else {
      deleteCookie("av_enabled");
    }
  }

  try {
    const postData = blogPostStore.getBlogPost(props.alias) ?? (await blogPostStore.fetchBlogPost(props.alias, locale.value));
    post.value = postData.post;
    relatedPosts.value = postData.related_posts;
    setTitle();
    setStructuredData();
  } catch (e: any) {
    if (e.status?.status === 404) {
      await router.push(`/${route.path.split("/")[1]}`);
    } else {
      throw e;
    }
  }

  amplitudePageViewTrack();
}

function setTitle() {
  document.title = getTitle(post.value?.[`title_${locale.value}`] as string, false);
}

function setStructuredData() {
  setBlogPostingJson();
  setBreadcrumbListJson();
}

const setBlogPostingJson = () => {
  const description = post.value?.[`description_${locale.value}`] || "";
  const articleBody = description
    .toString()
    .replace(/<\/?[^>]+(>|$)/g, "")
    .replace(/(\r\n|\n|\r)/gm, "");
  const formattedDate = getDate({ timestamp: post.value?.date as number });

  const json = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.value?.meta_title,
    image: post.value?.thumb,
    editor: "ERSTIES",
    genre: "porn",
    keywords: post.value?.meta_keywords ?? "",
    wordCount: articleBody.split(" ").length,
    url: window.location.origin + window.location.pathname,
    datePublished: formattedDate,
    dateCreated: formattedDate,
    dateModified: formattedDate,
    description: post.value?.meta_description ?? "",
    articleBody: articleBody,
    author: {
      "@type": "Person",
      name: "ERSTIES",
    },
    publisher: {
      "@type": "Organization",
      name: "ersties.com",
      logo: `${window.location.origin}/assets/images/logo-${locale.value}.svg`,
    },
  };

  const existingScript = document.getElementById("structured_data");

  if (existingScript) {
    existingScript.remove();
  }

  insertScript(
    null,
    {
      id: "structured_data",
      type: "application/ld+json",
    },
    JSON.stringify(json),
  );
};

const setBreadcrumbListJson = () => {
  const origin = window.location.origin;

  const json = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Ersties Home",
        item: origin,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Blog",
        item: `${origin}/${route.path.split("/")[1]}`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: post.value?.title_en,
        item: window.location.href,
      },
    ],
  };

  const existingScript = document.getElementById("breadcrumb");

  if (existingScript) {
    existingScript.remove();
  }

  insertScript(
    null,
    {
      id: "breadcrumb",
      type: "application/ld+json",
    },
    JSON.stringify(json),
  );
};

onBeforeMount(load);
onBeforeMount(() => {
  changeLanguage(route.path.indexOf("/blog") === 0 ? "en" : "de");
});
</script>
